/*
 * Bayeux cart: the cart drawer element and quantity / remove / note updates
 * for both the drawer and the cart page. Keeps the cart-drawer API Dawn's
 * product-form.js relies on (getSectionsToRender, renderContents, open,
 * setActiveElement).
 */

const BAYEUX_CART_DEBOUNCE = 350;

function bxSectionInner(html, selector = '.shopify-section') {
  const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  return node ? node.innerHTML : null;
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });

    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      if (document.body.classList.contains('template-cart')) return;
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code !== 'Space' || document.body.classList.contains('template-cart')) return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  get panel() {
    return this.querySelector('[data-drawer-panel]');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('is-open')) return;
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => this.trap());
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    else this.activeElement?.focus();
  }

  trap(focusTarget) {
    if (!this.classList.contains('is-open') || !this.panel) return;
    if (typeof trapFocus === 'function') trapFocus(this.panel, focusTarget || this.panel);
    else (focusTarget || this.panel).focus();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
  }

  renderContents(parsedState) {
    BayeuxCart.render(parsedState.sections);
    this.open();
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

const BayeuxCart = {
  timers: new Map(),

  mainSection() {
    return document.querySelector('[data-main-cart]');
  },

  sectionIds() {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    const main = this.mainSection();
    if (main) ids.push(main.dataset.sectionId);
    return ids;
  },

  render(sections) {
    if (!sections) return;
    const drawer = document.getElementById('CartDrawer');
    if (drawer && sections['cart-drawer']) {
      const inner = bxSectionInner(sections['cart-drawer'], '#CartDrawer');
      if (inner !== null) drawer.innerHTML = inner;
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const inner = bxSectionInner(sections['cart-icon-bubble']);
      if (inner !== null) bubble.innerHTML = inner;
    }
    const main = this.mainSection();
    if (main && sections[main.dataset.sectionId]) {
      const inner = bxSectionInner(sections[main.dataset.sectionId], '[data-main-cart]');
      if (inner !== null) main.innerHTML = inner;
    }
  },

  showError(scope, message) {
    const box = scope?.querySelector('[data-cart-error]') || document.querySelector('[data-cart-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = !message;
  },

  async change(line, quantity, focusSelector) {
    const row = line.closest('[data-line]');
    const key = row.dataset.line;
    const scope = row.closest('#CartDrawer') || this.mainSection();
    const inDrawer = Boolean(row.closest('#CartDrawer'));
    row.classList.add('is-loading');

    try {
      const response = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: this.sectionIds(), sections_url: window.location.pathname }),
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        row.classList.remove('is-loading');
        const input = row.querySelector('.quantity-stepper__input');
        if (input) input.value = input.defaultValue;
        this.showError(scope, state.description || state.message || window.cartStrings?.error || '');
        return;
      }

      this.render(state.sections);
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
      }

      const container = inDrawer ? document.getElementById('CartDrawer') : this.mainSection();
      const sameRow = container?.querySelector(`[data-line="${CSS.escape(key)}"]`);
      const target = (sameRow && focusSelector && sameRow.querySelector(focusSelector)) || null;
      if (inDrawer) {
        document.getElementById('CartDrawer')?.trap(target || undefined);
      } else if (target) {
        target.focus();
      } else {
        container?.querySelector('.cart-line__title, h1')?.focus?.();
      }
    } catch (error) {
      row.classList.remove('is-loading');
      this.showError(scope, window.cartStrings?.error || 'Something went wrong. Please try again.');
    }
  },

  queue(row, quantity, focusSelector) {
    const key = row.dataset.line;
    clearTimeout(this.timers.get(key));
    this.timers.set(
      key,
      setTimeout(() => {
        this.timers.delete(key);
        this.change(row, quantity, focusSelector);
      }, BAYEUX_CART_DEBOUNCE)
    );
  },

  saveNote: null,
};

document.addEventListener('click', (event) => {
  const row = event.target.closest('[data-line]');
  if (!row) return;

  const step = event.target.closest('[data-qty]');
  if (step) {
    const input = row.querySelector('.quantity-stepper__input');
    const next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.qty));
    input.value = next;
    BayeuxCart.queue(row, next, `[data-qty="${step.dataset.qty}"]`);
    return;
  }

  if (event.target.closest('[data-cart-remove]')) {
    event.preventDefault();
    BayeuxCart.change(row, 0);
  }
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-line] .quantity-stepper__input');
  if (!input) return;
  const value = Math.max(0, parseInt(input.value, 10) || 0);
  BayeuxCart.queue(input.closest('[data-line]'), value, '.quantity-stepper__input');
});

document.addEventListener('input', (event) => {
  const note = event.target.closest('[data-cart-note]');
  if (!note) return;
  clearTimeout(BayeuxCart.saveNote);
  BayeuxCart.saveNote = setTimeout(() => {
    fetch(`${routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: note.value }),
    });
  }, BAYEUX_CART_DEBOUNCE);
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-discount-form]');
  if (!form) return;
  event.preventDefault();
  const code = form.querySelector('input').value.trim();
  if (!code) return;
  window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(routes.cart_url)}`;
});
